/**
 * Хук для работы с AI чатом
 */

import { useState, useCallback, useEffect } from 'react';
import { useApiUrl } from '@refinedev/core';
import type {
  Chat,
  ChatWithMessages,
  ChatMessage,
  SendMessageResponse,
  PageContext,
  AiAssistantState,
  ModelSummary,
  Attachment,
  ErrorDetails,
} from './types';
import { formatPageContextForAgent } from './use-page-context';

const API_BASE = '/ai/admin';

/** Ошибка API с деталями */
class ApiError extends Error {
  details: ErrorDetails | null;

  constructor(message: string, details: ErrorDetails | null = null) {
    super(message);
    this.name = 'ApiError';
    this.details = details;
  }
}

interface UseAiChatResult {
  // Состояние
  state: AiAssistantState;
  chats: Chat[];
  currentChat: ChatWithMessages | null;
  messages: ChatMessage[];

  // Модели
  models: ModelSummary[];
  selectedModel: string | null;
  setSelectedModel: (model: string | null) => void;
  isLoadingModels: boolean;

  // Router Agent
  routerEnabled: boolean;
  setRouterEnabled: (enabled: boolean) => void;
  routerModel: string | null;
  setRouterModel: (model: string | null) => void;

  // Web Search
  webSearchEnabled: boolean;
  setWebSearchEnabled: (enabled: boolean) => void;

  // Knowledge Base
  selectedDocuments: number[];
  setSelectedDocuments: (ids: number[]) => void;

  // Контекст
  totalTokensUsed: number;
  contextLimit: number;
  contextUsagePercent: number;

  // Действия
  openPanel: () => void;
  closePanel: () => void;
  toggleMinimize: () => void;

  // Чаты
  loadChats: () => Promise<void>;
  createChat: () => Promise<string>;
  selectChat: (chatId: string) => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;
  clearContext: () => Promise<void>;
  startNewChat: () => Promise<void>;

  // Сообщения
  sendMessage: (message: string, pageContext?: PageContext, attachments?: Attachment[]) => Promise<void>;
  regenerateLastMessage: (pageContext?: PageContext) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  editMessage: (messageId: string, newContent: string, pageContext?: PageContext) => Promise<void>;
  switchBranch: (messageId: string, branchIndex: number) => Promise<void>;

  // Загрузка
  isLoadingChats: boolean;
  isLoadingMessages: boolean;
  isSending: boolean;
}

export function useAiChat(): UseAiChatResult {
  const apiUrl = useApiUrl();

  // Состояние панели
  const [state, setState] = useState<AiAssistantState>({
    isOpen: false,
    isMinimized: false,
    currentChatId: null,
    isLoading: false,
    error: null,
    errorDetails: null,
  });

  // Данные
  const [chats, setChats] = useState<Chat[]>([]);
  const [currentChat, setCurrentChat] = useState<ChatWithMessages | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // Модели
  const [models, setModels] = useState<ModelSummary[]>([]);
  const [selectedModel, setSelectedModel] = useState<string | null>(() => {
    // Восстанавливаем из localStorage
    return localStorage.getItem('ai-assistant-model');
  });
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  // Router Agent settings
  const [routerEnabled, setRouterEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('ai-assistant-router-enabled');
    return saved === null ? true : saved === 'true'; // По умолчанию включён
  });
  const [routerModel, setRouterModel] = useState<string | null>(() => {
    return localStorage.getItem('ai-assistant-router-model');
  });

  // Web Search
  const [webSearchEnabled, setWebSearchEnabled] = useState<boolean>(() => {
    return localStorage.getItem('ai-assistant-web-search') === 'true';
  });

  // Knowledge Base - выбранные документы для RAG
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);

  // Контекст - подсчёт использованных токенов
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);

  // Loading states
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Fetch helper - возвращает data из ResponseFormatter.success()
  // Глобальный интерцептор автоматически обрабатывает 401 и refresh токенов
  const fetchApi = useCallback(
    async <T>(
      endpoint: string,
      options: RequestInit = {}
    ): Promise<T> => {
      const response = await fetch(`${apiUrl}${API_BASE}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorResponse = await response.json().catch(() => ({ errors: ['Request failed'] }));
        const message = errorResponse.errors?.[0] || errorResponse.message || `HTTP ${response.status}`;
        const details = errorResponse.meta?.errorDetails as ErrorDetails | undefined;
        throw new ApiError(message, details || null);
      }

      const result = await response.json();
      // ResponseFormatter возвращает { success, data }, нам нужен data
      return result.data as T;
    },
    [apiUrl]
  );

  // ==================== Работа с моделями ====================

  const loadModels = useCallback(async () => {
    setIsLoadingModels(true);
    try {
      // Используем общий эндпоинт /ai/models (не admin)
      // Глобальный интерцептор автоматически добавляет Authorization header
      const response = await fetch(`${apiUrl}/ai/models`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load models');
      }

      const result = await response.json();
      // ResponseFormatter возвращает { success, data }
      if (result.success && Array.isArray(result.data)) {
        setModels(result.data as ModelSummary[]);
      }
    } catch (error) {
      console.error('Failed to load models:', error);
    } finally {
      setIsLoadingModels(false);
    }
  }, [apiUrl]);

  const handleSetSelectedModel = useCallback((model: string | null) => {
    setSelectedModel(model);
    if (model) {
      localStorage.setItem('ai-assistant-model', model);
    } else {
      localStorage.removeItem('ai-assistant-model');
    }
  }, []);

  const handleSetRouterEnabled = useCallback((enabled: boolean) => {
    setRouterEnabled(enabled);
    localStorage.setItem('ai-assistant-router-enabled', String(enabled));
  }, []);

  const handleSetRouterModel = useCallback((model: string | null) => {
    setRouterModel(model);
    if (model) {
      localStorage.setItem('ai-assistant-router-model', model);
    } else {
      localStorage.removeItem('ai-assistant-router-model');
    }
  }, []);

  const handleSetWebSearchEnabled = useCallback((enabled: boolean) => {
    setWebSearchEnabled(enabled);
    localStorage.setItem('ai-assistant-web-search', String(enabled));
  }, []);

  const handleSetSelectedDocuments = useCallback((ids: number[]) => {
    setSelectedDocuments(ids);
  }, []);

  // ==================== Действия с панелью ====================

  const openPanel = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: true, isMinimized: false }));
  }, []);

  const closePanel = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const toggleMinimize = useCallback(() => {
    setState(prev => ({ ...prev, isMinimized: !prev.isMinimized }));
  }, []);

  // ==================== Работа с чатами ====================

  const loadChats = useCallback(async () => {
    setIsLoadingChats(true);
    try {
      const response = await fetchApi<{ chats: Chat[]; total: number }>('/chats');
      setChats(response.chats || []);
    } catch (error) {
      console.error('Failed to load chats:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load chats',
      }));
    } finally {
      setIsLoadingChats(false);
    }
  }, [fetchApi]);

  const createChat = useCallback(async (): Promise<string> => {
    try {
      const response = await fetchApi<{ id: string; title: string | null }>(
        '/chats',
        { method: 'POST', body: JSON.stringify({}) }
      );

      // Добавляем новый чат в список
      const newChat: Chat = {
        id: response.id,
        title: response.title,
        isArchived: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastMessageAt: null,
        messageCount: 0,
      };

      setChats(prev => [newChat, ...prev]);
      setState(prev => ({ ...prev, currentChatId: response.id }));
      setCurrentChat({ ...newChat, messages: [] });
      setMessages([]);

      return response.id;
    } catch (error) {
      console.error('Failed to create chat:', error);
      throw error;
    }
  }, [fetchApi]);

  const selectChat = useCallback(
    async (chatId: string) => {
      setIsLoadingMessages(true);
      setState(prev => ({ ...prev, currentChatId: chatId }));

      try {
        const response = await fetchApi<ChatWithMessages>(`/chats/${chatId}`);
        setCurrentChat(response);
        const loadedMessages = (response.messages || []).map(m => ({
          ...m,
          createdAt: new Date(m.createdAt),
        }));
        setMessages(loadedMessages);

        // Находим последнее сообщение с токенами и берём его total как текущий размер контекста
        const lastMessageWithTokens = [...loadedMessages]
          .reverse()
          .find(m => m.metadata?.tokens?.total);
        if (lastMessageWithTokens?.metadata?.tokens?.total) {
          setTotalTokensUsed(lastMessageWithTokens.metadata.tokens.total);
        } else {
          setTotalTokensUsed(0);
        }
      } catch (error) {
        console.error('Failed to load chat:', error);
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Failed to load chat',
        }));
      } finally {
        setIsLoadingMessages(false);
      }
    },
    [fetchApi]
  );

  const deleteChat = useCallback(
    async (chatId: string) => {
      try {
        await fetchApi(`/chats/${chatId}`, { method: 'DELETE' });
        setChats(prev => prev.filter(c => c.id !== chatId));

        if (state.currentChatId === chatId) {
          setState(prev => ({ ...prev, currentChatId: null }));
          setCurrentChat(null);
          setMessages([]);
          setTotalTokensUsed(0);
        }
      } catch (error) {
        console.error('Failed to delete chat:', error);
        throw error;
      }
    },
    [fetchApi, state.currentChatId]
  );

  /** Очистка контекста текущего чата (удаляет сообщения, но сохраняет чат) */
  const clearContext = useCallback(async () => {
    if (!state.currentChatId) {return;}

    try {
      await fetchApi(`/chats/${state.currentChatId}/clear`, { method: 'POST' });
      setMessages([]);
      setTotalTokensUsed(0);

      // Обновляем список чатов (title сбросился)
      loadChats();
    } catch (error) {
      console.error('Failed to clear context:', error);
      throw error;
    }
  }, [state.currentChatId, fetchApi, loadChats]);

  /** Создать новый чат (для кнопки "Новый чат") */
  const startNewChat = useCallback(async () => {
    await createChat();
    setTotalTokensUsed(0);
  }, [createChat]);

  // ==================== Отправка сообщений ====================

  const sendMessage = useCallback(
    async (
      message: string,
      pageContext?: PageContext,
      attachments?: Attachment[],
      /** Пропустить добавление user message (используется при edit) */
      skipAddUserMessage?: boolean
    ) => {
      if (!message.trim() && (!attachments || attachments.length === 0)) {return;}

      // Если нет текущего чата - создаём
      let chatId = state.currentChatId;
      if (!chatId) {
        chatId = await createChat();
      }

      // Добавляем user message локально (если не пропускаем)
      const userMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content: message,
        attachments: attachments?.map(a => ({ url: a.url, mimeType: a.mimeType, filename: a.filename })),
        createdAt: new Date(),
      };
      if (!skipAddUserMessage) {
        setMessages(prev => [...prev, userMessage]);
      }

      setIsSending(true);
      setState(prev => ({ ...prev, error: null, errorDetails: null }));

      try {
        // Передаём pageContext отдельным полем (скрыто от пользователя)
        const pageContextStr = pageContext
          ? formatPageContextForAgent(pageContext)
          : undefined;

        const response = await fetchApi<SendMessageResponse>(
          `/chats/${chatId}/messages`,
          {
            method: 'POST',
            body: JSON.stringify({
              message, // Чистое сообщение без контекста
              model: selectedModel || undefined,
              pageContext: pageContextStr, // Контекст передаётся отдельно
              // Router Agent settings
              routerEnabled,
              routerModel: routerModel || undefined,
              // Attachments для vision
              attachments: attachments?.filter(a => a.status === 'ready').map(a => ({
                id: a.id,
                url: a.url,
                mimeType: a.mimeType,
              })),
              // Web Search
              webSearchEnabled,
              // Knowledge Base
              selectedDocumentIds: selectedDocuments.length > 0 ? selectedDocuments : undefined,
            }),
          }
        );

        // Добавляем assistant message с полной metadata
        const assistantMessage: ChatMessage = {
          id: response.messageId,
          role: 'assistant',
          content: response.answer,
          metadata: response.metadata ?? {
            toolsUsed: response.toolsUsed,
          },
          createdAt: new Date(),
        };

        setMessages(prev => [...prev, assistantMessage]);

        // Обновляем подсчёт токенов - total это общий размер контекста в этом запросе
        // Используем его как текущее значение, а не накапливаем
        if (response.metadata?.tokens?.total) {
          setTotalTokensUsed(response.metadata.tokens.total);
        }

        // Обновляем список чатов (title мог измениться)
        loadChats();
      } catch (error) {
        console.error('Failed to send message:', error);

        // Извлекаем детали ошибки если это ApiError
        const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
        const errorDetails = (error as { details?: ErrorDetails })?.details || null;

        setState(prev => ({
          ...prev,
          error: errorMessage,
          errorDetails,
        }));

        // Убираем временное сообщение при ошибке (только если добавляли)
        if (!skipAddUserMessage) {
          setMessages(prev => prev.filter(m => m.id !== userMessage.id));
        }
      } finally {
        setIsSending(false);
      }
    },
    [state.currentChatId, createChat, fetchApi, loadChats, selectedModel, routerEnabled, routerModel, webSearchEnabled, selectedDocuments]
  );

  /** Перегенерация последнего ответа ассистента */
  const regenerateLastMessage = useCallback(
    async (pageContext?: PageContext) => {
      // Находим последнее сообщение пользователя
      const lastUserMessageIndex = [...messages].reverse().findIndex(m => m.role === 'user');
      if (lastUserMessageIndex === -1) {return;}

      const actualIndex = messages.length - 1 - lastUserMessageIndex;
      const lastUserMessage = messages[actualIndex];
      if (!lastUserMessage?.content) {return;}

      // Удаляем user message и все сообщения после него
      // sendMessage добавит user message заново
      setMessages(prev => prev.slice(0, actualIndex));

      // Отправляем заново - токены обновятся автоматически после ответа
      await sendMessage(lastUserMessage.content, pageContext);
    },
    [messages, sendMessage]
  );

  /** Удаление сообщения */
  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!state.currentChatId) {return;}

      try {
        await fetchApi(`/chats/${state.currentChatId}/messages/${messageId}`, {
          method: 'DELETE',
        });

        // Перезагружаем чат чтобы получить актуальное состояние
        await selectChat(state.currentChatId);
      } catch (error) {
        console.error('Failed to delete message:', error);
        throw error;
      }
    },
    [state.currentChatId, fetchApi, selectChat]
  );

  /** Редактирование сообщения (создаёт новую ветку и перегенерирует ответ) */
  const editMessage = useCallback(
    async (messageId: string, newContent: string, pageContext?: PageContext) => {
      if (!state.currentChatId) {return;}

      try {
        // Редактируем сообщение на сервере (создаёт новую ветку)
        await fetchApi<{ newMessageId: string; branchIndex: number }>(
          `/chats/${state.currentChatId}/messages/${messageId}`,
          {
            method: 'PUT',
            body: JSON.stringify({ content: newContent }),
          }
        );

        // Перезагружаем чат чтобы получить новую ветку
        await selectChat(state.currentChatId);

        // Отправляем отредактированное сообщение для получения нового ответа
        // Передаём skipAddUserMessage=true, чтобы не добавлять user сообщение повторно
        // sendMessage сам управляет isSending
        await sendMessage(newContent, pageContext, undefined, true);
      } catch (error) {
        console.error('Failed to edit message:', error);
        throw error;
      }
    },
    [state.currentChatId, fetchApi, selectChat, sendMessage]
  );

  /** Переключение между ветками */
  const switchBranch = useCallback(
    async (messageId: string, branchIndex: number) => {
      if (!state.currentChatId) {return;}

      try {
        await fetchApi(`/chats/${state.currentChatId}/messages/${messageId}/switch-branch`, {
          method: 'POST',
          body: JSON.stringify({ branchIndex }),
        });

        // Перезагружаем чат чтобы получить актуальную ветку
        await selectChat(state.currentChatId);
      } catch (error) {
        console.error('Failed to switch branch:', error);
        throw error;
      }
    },
    [state.currentChatId, fetchApi, selectChat]
  );

  // ==================== Эффекты ====================

  // Загружаем чаты при открытии панели
  useEffect(() => {
    if (state.isOpen && chats.length === 0) {
      loadChats();
    }
  }, [state.isOpen, chats.length, loadChats]);

  // Загружаем модели при открытии панели
  useEffect(() => {
    if (state.isOpen && models.length === 0) {
      loadModels();
    }
  }, [state.isOpen, models.length, loadModels]);

  // Вычисляем лимит контекста на основе выбранной модели
  const selectedModelInfo = models.find(m => m.id === selectedModel);
  const contextLimit = selectedModelInfo?.contextLength || 128000; // По умолчанию 128k
  const contextUsagePercent = contextLimit > 0 ? Math.min(100, (totalTokensUsed / contextLimit) * 100) : 0;

  return {
    state,
    chats,
    currentChat,
    messages,

    // Модели
    models,
    selectedModel,
    setSelectedModel: handleSetSelectedModel,
    isLoadingModels,

    // Router Agent
    routerEnabled,
    setRouterEnabled: handleSetRouterEnabled,
    routerModel,
    setRouterModel: handleSetRouterModel,

    // Web Search
    webSearchEnabled,
    setWebSearchEnabled: handleSetWebSearchEnabled,

    // Knowledge Base
    selectedDocuments,
    setSelectedDocuments: handleSetSelectedDocuments,

    // Контекст
    totalTokensUsed,
    contextLimit,
    contextUsagePercent,

    openPanel,
    closePanel,
    toggleMinimize,

    loadChats,
    createChat,
    selectChat,
    deleteChat,
    clearContext,
    startNewChat,

    sendMessage,
    regenerateLastMessage,
    deleteMessage,
    editMessage,
    switchBranch,

    isLoadingChats,
    isLoadingMessages,
    isSending,
  };
}
